import { z } from 'zod';

// Enum for pricing methods
export enum PricingMethodLiteral {
  PROFIT_PERCENTAGE = 'profit_percentage',
  FIXED_PROFIT = 'fixed_profit',
  FIXED_SALE_PRICE = 'fixed_sale_price',
  FIXED = 'fixed', // Backend uses 'fixed' instead of 'fixed_sale_price'
}

// Interface for ItemSalePriceRule
export interface ItemSalePriceRule {
  id: number;
  item_id: number;
  organization_id: number;
  site_id: number | null;
  pricing_method: PricingMethodLiteral | string;
  value: number | string; // Can be percentage, fixed profit amount, or fixed sale price
  is_active: boolean;
  min_qty?: number | string | null;
  max_qty?: number | string | null;
  effective_from?: string | null;
  effective_to?: string | null;
  created_at: string;
  updated_at: string | null;
}

// Zod schema for ItemSalePriceRule
export const ItemSalePriceRuleSchema = z.object({
  id: z.number(),
  item_id: z.number(),
  organization_id: z.number(),
  site_id: z.number().nullable(),
  pricing_method: z.union([z.nativeEnum(PricingMethodLiteral), z.string()]),
  value: z.union([z.number(), z.string()]),
  is_active: z.boolean(),
  min_qty: z.union([z.number().nullable(), z.string().nullable()]),
  max_qty: z.union([z.number().nullable(), z.string().nullable()]),
  effective_from: z.string().nullable(),
  effective_to: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string().nullable(),
});

// Type for filtering and querying item sale price rules
export interface ItemSalePriceRuleQueryParams {
  page?: number;
  limit?: number;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  filters?: string; // JSON string of filter objects
  query?: string;
}

// API response format for listing item sale price rules
export interface ItemSalePriceRuleListResponse {
  items: ItemSalePriceRule[];
  total: number;
  page: number;
  limit: number;
}

export type CreateItemSalePriceRuleInput = Omit<ItemSalePriceRule, 'id' | 'organization_id' | 'created_at' | 'updated_at'>;
export type UpdateItemSalePriceRuleInput = Partial<Omit<ItemSalePriceRule, 'id' | 'organization_id' | 'created_at' | 'updated_at'>>;

export interface TaxRateWithComponents {
  id: number;
  name: string;
  code?: string;
  rate: string | number;
  components?: Array<{
    id?: number;
    code?: string;
    rate: string | number;
    tax_component_id?: number;
  }>;
} 